import { User } from '../../../../domain/models';
import {
  Animations,
  Button,
  ModalController,
  Select,
  Spinners,
  useToast,
} from '@keymax-dev/smartepi-ui';
import { motion, useAnimation } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import PageTitle from '../../../../components/page-title';
import useRest from '../../../../hooks/use-rest';
import useRestSearchSelect from '../../../../hooks/use-rest-search-select';
import { ErrorResponse } from '../../../../domain/models';
import { EndPoints } from '../../../../utils/constants';
import { getErrorMessage } from '../../../../utils/error-messages';
import { AddEmployeeCostCenterModalElement } from './style';

enum Content {
  Form,
  Loading,
}

interface AddCostCenterModalProps {
  controller?: ModalController;
  costCenterId: string;
}

const { usePut } = useRest();

const Strings = {
  Buttons: {
    Add: 'Adicionar',
  },
  Inputs: {
    Employee: 'Funcionário',
  },
  Messages: {
    RegisterLoading: 'Adicionando funcionário...',
    RegisterSuccess: 'Funcionário adicionado com sucesso!',
    UnknownError:
      'Ocorreu um erro inesperado. Por favor, verifique sua conexão com a internet.',
    InvalidForm: 'Os dados fornecidos são inválidos!',
    UserNotSelected: 'Selecione um funcionário para adicionar',
  },
  Title: 'Adicionar Funcionário ao Centro de Custo',
};

export default function AddEmployeeCostCenterModal({
  controller,
  costCenterId,
}: AddCostCenterModalProps): JSX.Element {
  const [putData, put] = usePut({ loading: false });
  const errorToast = useToast(<span />, { color: 'danger' });
  const successToast = useToast(
    <span>{Strings.Messages.RegisterSuccess}</span>,
    { color: 'success' }
  );
  const {
    loading,
    searchHandler,
    searchedList,
    state: [value, setValue],
  } = useRestSearchSelect<User.Data>({
    endpoint: EndPoints.Users,
    config: {
      requestFilters: [
        {
          filter: 'costCenter',
          filterValue: costCenterId,
          contain: false,
        },
      ],
    },
  });

  const contentAnimation = useAnimation();
  const [content, _setContent] = useState<Content>(Content.Form);
  const setContent = (value: Content): void => {
    contentAnimation
      .start(Animations.FadeOut)
      .then(() => Promise.resolve(_setContent(value)))
      .then(() => contentAnimation.start(Animations.FadeIn));
  };

  const requestSuccessHandler = (data: User.Data): void => {
    setTimeout(() => successToast.open());
    if (controller) controller.close(data);
  };

  const requestErrorHandler = (error: ErrorResponse): void => {
    errorToast.setContent(getErrorMessage(error));
    setTimeout(() => errorToast.open());
  };

  const confirmHandler = (): void => {
    if (value) {
      put(EndPoints.User(value._id), { costCenterId: [costCenterId] });
    } else {
      errorToast.setContent(Strings.Messages.UserNotSelected);
      setTimeout(() => errorToast.open());
    }
  };

  useEffect(() => {
    if (putData.loading) {
      setContent(Content.Loading);
    } else {
      setContent(Content.Form);
      if (putData.payload) requestSuccessHandler(putData.payload.data);
      else if (putData.error) requestErrorHandler(putData.error);
    }
  }, [putData]);

  return (
    <AddEmployeeCostCenterModalElement>
      <PageTitle>{Strings.Title}</PageTitle>

      {content === Content.Form && (
        <motion.div className="__form-container">
          <div className="__form-container">
            <Select<User.Data>
              data={searchedList}
              dataKey="name"
              loading={loading}
              onSearch={searchHandler}
              onSelect={(event, value): void => setValue(value)}
              placeholder={Strings.Inputs.Employee}
              value={value}
            />
          </div>
          <Button
            text={Strings.Buttons.Add}
            color="success"
            onClick={confirmHandler}
          />
        </motion.div>
      )}
      {content === Content.Loading && (
        <motion.div
          className="__form-container"
          animate={contentAnimation}
          initial={{ opacity: 0 }}
        >
          <Spinners.circles width="300px" height="300px" />
          <span>{Strings.Messages.RegisterLoading}</span>
        </motion.div>
      )}
    </AddEmployeeCostCenterModalElement>
  );
}
